import React, { useEffect, useState, useCallback } from 'react';
import { useAppDispatch } from 'redux/hooks';
import { fetchActiveTeams, fetchCountStatistics } from 'thunk';
import {
  countStatisticsSearchRangeHash,
  COUNT_STATISTICS_DATA_TYPE,
  COUNT_STATISTICS_SEARCH_RANGE,
  TEAM_CREATION_RANGE_OPTIONS,
  TEAM_CREATION_RANGE_OPTIONS_HASH,
} from 'StatisticsModule/constants';
import { format, parseISO } from 'date-fns';
import { calculateCountStatisticsDateParamsFromOption } from 'StatisticsModule/utils';
import { Item } from 'types';
import { ValueOf } from 'type-fest';

import Dashboard from 'StatisticsModule/components/CountStatistics/Dashboard';
import { PromiseAllSettledStatus } from 'services/constants';
import { FetchCountStatisticsResponseData } from 'StatisticsModule/types';
import useLocalDispatch from './useLocalDispatch';
import { FetchActiveTeamsSuccessResponse } from 'ReportsModule/types';
import { TeamId } from 'IntegrationModule/types';

const formatCustomDateLabel = (dateStr?: string) =>
  dateStr ? format(parseISO(dateStr), 'MMM dd, yyyy') : '';

const transformCountStatisticsResponseToHash = (
  responseArray: {
    status: ValueOf<typeof PromiseAllSettledStatus.Fulfilled>;
    value: {
      data: FetchCountStatisticsResponseData;
      config: {
        params: {
          search_range_date?: string;
        };
      };
    };
  }[],
) => {
  const hash: Record<string, FetchCountStatisticsResponseData> = {};

  for (let i = 0; i < responseArray.length; i++) {
    const currentRange = responseArray[i];
    if (PromiseAllSettledStatus.Fulfilled === currentRange?.status) {
      const key =
        currentRange.value.config.params.search_range_date || COUNT_STATISTICS_SEARCH_RANGE.CUSTOM; // if search_range_date not provided, this is a custom range
      hash[key] = currentRange.value.data;
    }
  }

  return hash;
};

type HookProps = {
  teamIds?: number[];
  teamTags?: string[];
  shouldUseTeamCreationBounds?: boolean;
  dataTypeToIgnoreSet?: Set<ValueOf<typeof COUNT_STATISTICS_DATA_TYPE>>;
};

const defaultTeamCreationDateOption = TEAM_CREATION_RANGE_OPTIONS.ALL_TEAMS;

const stateId = 'fetchActiveTeams';

const useCountStatisticalDashboard = ({
  teamIds,
  teamTags,
  shouldUseTeamCreationBounds = true,
  dataTypeToIgnoreSet,
}: HookProps) => {
  const dispatch = useAppDispatch();
  const [dashboardFilters, setDashboardFilters] = useState({
    searchDateRange: COUNT_STATISTICS_SEARCH_RANGE.LAST_WEEK,
    teamCreationDateOption: defaultTeamCreationDateOption,
    customDateRange: {
      tempCustomStart: '',
      tempCustomEnd: '',
      customStart: '',
      customEnd: '',
    },
  });
  const [countStatisticsState, setCountStatisticsState] = useState<{
    data: ReturnType<typeof transformCountStatisticsResponseToHash>;
    isRequesting: boolean;
  }>({
    data: {},
    isRequesting: false,
  });
  const {
    shortLabel: selectedSearchDateRangeShortLabel,
    value: selectedSearchDateRange,
    date: activeUserSinceDate,
  } = countStatisticsSearchRangeHash[dashboardFilters['searchDateRange']];
  const { customEnd, customStart, tempCustomEnd, tempCustomStart } =
    dashboardFilters['customDateRange'];
  const { value: teamCreationDateOption } =
    TEAM_CREATION_RANGE_OPTIONS_HASH[dashboardFilters['teamCreationDateOption']];
  const { dispatchLocal, selectLocalData } = useLocalDispatch();

  const triggerCountStatisticsFilter = useCallback(
    (params: {
      teamIds?: number[];
      teamTags?: string[];
      teamCreationDateOption?: ValueOf<typeof TEAM_CREATION_RANGE_OPTIONS>;
      customOnly?: boolean;
    }) => {
      const { lowerBound, upperBound } = calculateCountStatisticsDateParamsFromOption(
        params.teamCreationDateOption ||
          dashboardFilters.teamCreationDateOption ||
          defaultTeamCreationDateOption,
      );

      const isCustomRangeValid = !!(tempCustomEnd && tempCustomStart);

      setCountStatisticsState((prev) => ({
        ...prev,
        isRequesting: true,
      }));

      dispatch(
        fetchCountStatistics({
          team_creation_date_lower_bound: shouldUseTeamCreationBounds ? lowerBound : undefined,
          team_creation_date_upper_bound: shouldUseTeamCreationBounds ? upperBound : undefined,
          team_ids: params.teamIds || teamIds || [],
          team_tags: params.teamTags || teamTags || [],
          customOnly: params.customOnly,
          customEnd: tempCustomEnd,
          customStart: tempCustomStart,
          isCustomRangeValid,
          onSuccess: ({ response }) => {
            setCountStatisticsState((prev) => ({
              data: {
                ...prev.data,
                ...transformCountStatisticsResponseToHash(response),
              },

              isRequesting: false,
            }));
            // Setting the main start and end date after fetch
            setDashboardFilters((prev) => ({
              ...prev,
              customDateRange: {
                ...prev.customDateRange,
                ...(isCustomRangeValid
                  ? {
                      customEnd: tempCustomEnd,
                      customStart: tempCustomStart,
                    }
                  : {}),
              },
            }));
          },
          onFailure: ({ error }) => {
            setCountStatisticsState((prev) => ({
              ...prev,
              isRequesting: false,
            }));
          },
        }),
      );
    },
    [
      dashboardFilters.teamCreationDateOption,
      dispatch,
      shouldUseTeamCreationBounds,
      teamIds,
      teamTags,
      tempCustomEnd,
      tempCustomStart,
    ],
  );

  useEffect(() => {
    // When click custom date range but not set start date yet, don't fetch
    if (selectedSearchDateRange === COUNT_STATISTICS_SEARCH_RANGE.CUSTOM && !customStart) {
      return;
    }

    dispatchLocal(fetchActiveTeams, {
      // fetch when date range change
      params: {
        date_filter: countStatisticsSearchRangeHash[selectedSearchDateRange]?.date || customStart,
        give_inactive_teams: false,
      },
      shouldUseMeta: true,
      formatSuccessResponse({ response }) {
        const activeUsersCounts = response.data.stats_res as FetchActiveTeamsSuccessResponse;

        return activeUsersCounts;
      },
      stateId,
    });
  }, [customStart, dispatchLocal, selectedSearchDateRange]);

  const { data: activeUsersCounts, isRequesting: isRequestingActiveCounts } = selectLocalData<
    Record<TeamId, { active_users_count: number }>
  >({
    stateId,
  });

  const totalActiveUserCount = activeUsersCounts
    ? Object.values(activeUsersCounts).reduce(
        (acc, { active_users_count }) => (acc += active_users_count),
        0,
      )
    : 0;

  const teamActiveUsersCounts =
    activeUsersCounts && teamIds && teamIds.length > 0
      ? activeUsersCounts[teamIds[0]]?.active_users_count
      : 0;

  const activeUserCountsToShow = isRequestingActiveCounts
    ? 'Loading...'
    : teamIds
      ? teamActiveUsersCounts
      : totalActiveUserCount;

  const onSelectSearchRangeDate = ({ item }: { item: Item }) => {
    setDashboardFilters((prev) => ({
      ...prev,
      searchDateRange: item.value as keyof typeof COUNT_STATISTICS_SEARCH_RANGE,
    }));
  };

  // For setting temporary custom range, only override the main date when press search (i.e: calling triggerCountStatisticsFilter)
  const onSelectCustomSearchRange = ({
    name,
    value,
  }: {
    name: 'tempCustomStart' | 'tempCustomEnd';
    value: string;
  }) => {
    setDashboardFilters((prev) => ({
      ...prev,
      customDateRange: {
        ...prev.customDateRange,
        [name]: value,
      },
    }));
  };

  const onSelectTeamCreationDate = ({
    option,
  }: {
    option: ValueOf<typeof TEAM_CREATION_RANGE_OPTIONS>;
  }) => {
    triggerCountStatisticsFilter({
      teamCreationDateOption: option,
    });
    setDashboardFilters((prev) => ({
      ...prev,
      teamCreationDateOption: option,
    }));
  };

  const searchDateRangeLabelToUse =
    selectedSearchDateRange === COUNT_STATISTICS_SEARCH_RANGE.CUSTOM
      ? `${formatCustomDateLabel(customStart)} - ${formatCustomDateLabel(customEnd)}`
      : selectedSearchDateRangeShortLabel;

  const activeUserDateToUse =
    selectedSearchDateRange === COUNT_STATISTICS_SEARCH_RANGE.CUSTOM
      ? customStart
      : activeUserSinceDate;

  return {
    triggerCountStatisticsFilter,
    onSelectSearchRangeDate,
    onSelectTeamCreationDate,
    dashboardFilters,
    teamCreationDateOption,
    onSelectCustomSearchRange,
    isFetchingCountStatistic: countStatisticsState.isRequesting,
    customEnd,
    customStart,
    tempCustomEnd,
    tempCustomStart,
    selectedSearchDateRange,
    Dashboard: (
      <Dashboard
        data={countStatisticsState.data?.[dashboardFilters.searchDateRange] || {}}
        isLoading={countStatisticsState.isRequesting}
        searchDateRangeLabel={searchDateRangeLabelToUse}
        dataTypeToIgnoreSet={dataTypeToIgnoreSet}
        activeUserCount={activeUserCountsToShow}
        activeUserSinceDate={activeUserDateToUse}
      />
    ),
  };
};

export default useCountStatisticalDashboard;
